<template>
  <div :qaid="`nav-list-${uid}`" class="nav-list-vue">
    <h4 v-if="columnHeader" class="nav-list-vue__column-header" v-html="columnHeader" />
    <ul
      :class="[
        'nav-list-vue__ul',
        {
          'nav-list-vue__ul--columns': colSpan,
          [`nav-list-vue__ul--col-count-${colSpan}`]: colSpan,
          [`nav-list-vue__ul--col-available-${colsAvailable}`]: colsAvailable,
        },
      ]"
    >
      <li
        v-for="(link, index) in links"
        :key="link.uid"
        :class="['nav-list-vue__li', {'nav-list-vue__li--columns': colSpan}]"
      >
        <div :class="['nav-list-vue__li-content', {'nav-list-vue__li-content--last-item': index === links.length - 1}]">
          <!-- TODO: in Phase 2, include this as the icon component  -->
          <svg
            v-if="link.linkNavMenuIconName"
            class="nav-list-vue__icon"
            xmlns="http://www.w3.org/2000/svg"
            height="18px"
            viewBox="0 0 24 24"
            width="18px"
          >
            <path
              d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"
            />
          </svg>

          <a
            :id="generateLinkId(link.uid)"
            :qaid="generateLinkId(link.uid)"
            role="link"
            :href="composeLink(link.url)"
            :target="setTarget(link.target)"
            class="nav-list-vue__link"
          >
            <span v-html="link.linkName" />
          </a>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import links from '../mixins/links';

export default {
  name: 'NavList',
  mixins: [links],
  inject: ['idSuffix'],
  props: {
    columnHeader: {
      type: String,
      default: null,
    },
    links: {
      type: Array,
      required: true,
    },
    uid: {
      type: String,
      required: true,
    },
    colSpan: {
      type: Number,
      default: null,
    },
    colsAvailable: {
      type: Number,
      default: null,
    },
  },
  methods: {
    generateLinkId(linkId) {
      return this.idSuffix ? `'nav-link-'${linkId}${this.idSuffix}` : `'nav-link-'${linkId}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.nav-list-vue {
  @apply px-2;
  --column-gap: 2rem;

  @include breakpoint(medium) {
    @apply px-3;
  }

  @include breakpoint(large) {
    @apply px-0;
  }

  &__column-header {
    @apply mb-2;
    @apply text-white;
    @apply leading-normal;
    @apply text-md;
    @apply font-bold;
    display: inline-block;

    &::before {
      content: '';
      border-left: 2px solid var(--nav-highlight-color);
      margin-right: 0.5rem;
    }

    @include breakpoint(large) {
      @apply text-sm;
    }
  }

  &__ul {
    &--columns {
      column-gap: var(--gap);
    }

    @include breakpoint(large) {
      &--col-count-2 {
        column-count: 2;

        &.nav-list-vue__ul--col-available-3 {
          width: calc((2 / 3 * 100%) - ((1 / 3) * var(--column-gap)));
        }

        &.nav-list-vue__ul--col-available-4 {
          width: calc((2 / 4 * 100%) - ((2 / 4) * var(--column-gap)));
        }
      }

      &--col-count-3 {
        column-count: 3;

        &.nav-list-vue__ul--col-available-4 {
          width: calc((3 / 4 * 100%) - ((1 / 4) * var(--column-gap)));
        }
      }

      &--col-count-4 {
        column-count: 4;
      }
    }
  }

  &__li {
    &--columns {
      break-inside: avoid;
    }
  }

  // Padding needs to be applied to an element inside the break-inside rules so that the columns dont split margins in safari/firefox
  &__li-content {
    padding-bottom: 0.75rem;
    display: flex;
    align-items: center;

    @include breakpoint(large) {
      align-items: flex-start;
    }

    &--last-item {
      @apply pb-0;
    }
  }

  &__icon {
    @apply -ml-25;
    display: inline-block;
    flex: none;
    fill: hsla(65, 50%, 50%, 1);
  }

  &__link {
    @apply text-gray-30;
    @apply font-medium;
    @apply text-md;
    display: inline-block;
    line-height: 1.2;
    transition: all 0.25s ease-out;

    &:focus {
      color: var(--nav-highlight-color);
    }

    @include breakpoint(large) {
      @apply text-sm;

      &:hover {
        color: var(--nav-highlight-color);
      }
    }
  }
}
</style>
